<script setup lang="ts">
// Free-text search filter. Holds the typed value locally and only
// reports it to the host after the user pauses (default 300 ms).
// Debouncing belongs here rather than in the worker because every
// keystroke would otherwise trigger a full insight re-run, blowing
// out the query batch and the network.

import { computed, onBeforeUnmount, ref, useId, watch } from "vue";

import type { TextOpts } from "../sbi/protocol";
import { usePrintMode } from "../sbi/print-mode.ts";
import PrintStaticControl from "./PrintStaticControl.vue";
import ControlLabel from "./ControlLabel.vue";

const props = defineProps<{
  id: string;
  value: string;
  opts?: TextOpts;
}>();

const emit = defineEmits<{
  (e: "change-target", id: string, value: string): void;
}>();

// Local typed value — diverges from `props.value` between keystrokes
// and resyncs whenever the host pushes a new authoritative value.
const local = ref(props.value);
let debounceTimer: ReturnType<typeof setTimeout> | null = null;

const isPrint = usePrintMode();

// Label wiring: ControlLabel puts `labelId` on the label text and points
// its `<label for>` at `controlId`.
const labelId = useId();
const controlId = useId();

// Print-mode display: the current query string verbatim, or "(empty)"
// when unset. The live UI's debounce is irrelevant on paper — we
// always show whatever the host pushed via `props.value`.
const printValue = computed(() => props.value || "(empty)");

watch(
  () => props.value,
  (next) => {
    if (next !== local.value) local.value = next;
  },
);

function onInput(e: Event): void {
  local.value = (e.target as HTMLInputElement).value;
  const delay = props.opts?.debounceMs ?? 300;
  if (debounceTimer !== null) clearTimeout(debounceTimer);
  debounceTimer = setTimeout(() => {
    emit("change-target", props.id, local.value);
    debounceTimer = null;
  }, delay);
}

onBeforeUnmount(() => {
  if (debounceTimer !== null) clearTimeout(debounceTimer);
});
</script>

<template>
  <PrintStaticControl
    v-if="isPrint()"
    :label="opts?.label"
    :value="printValue"
  />
  <div v-else class="search">
    <ControlLabel
      v-if="opts?.label || opts?.description"
      class="search-label"
      :label="opts?.label"
      :description="opts?.description"
      :for-id="controlId"
      :label-id="labelId"
    />
    <input
      :id="controlId"
      type="search"
      :value="local"
      :placeholder="opts?.placeholder"
      @input="onInput"
    />
  </div>
</template>

<style scoped>
/* Never wider than the slot it sits in; full width when that slot is
   narrow (filter bar < 480px, a narrow column cell). */
.search {
  max-width: 100%;
  min-width: 0;
  box-sizing: border-box;
  display: inline-flex;
  flex-direction: column;
  gap: 4px;
  font-size: 13px;
}
@container (max-width: 479px) {
  .search {
    display: flex;
    width: 100%;
  }
}
.search-label {
  color: var(--color-text-muted);
  font-size: 12px;
  display: inline-flex;
  align-items: center;
  gap: 4px;
}
input[type="search"] {
  background: var(--card);
  color: var(--card-ink);
  border: 1px solid var(--rule);
  border-radius: var(--radius);
  padding: 6px 10px;
  font: inherit;
  min-width: min(16ch, 100%);
  max-width: 100%;
  box-sizing: border-box;
}
input[type="search"]:focus {
  outline: 2px solid var(--primary);
  outline-offset: 1px;
}
/* Touch: 16px text (iOS zooms the page when a smaller input takes
   focus) and 40–44px targets. Desktop keeps the 13px density. */
@media (pointer: coarse) {
  input[type="search"] {
    min-height: 44px;
    font-size: 16px;
  }
}
</style>
